// Cookie utility functions for authentication

/**
 * Sets a cookie with the given name, value and options
 */
export function setCookie(
  name: string,
  value: string,
  options: {
    days?: number;
    path?: string;
    domain?: string;
    sameSite?: 'Strict' | 'Lax' | 'None';
    secure?: boolean;
  } = {}
): void {
  if (typeof window === 'undefined') return;

  const { days = 7, path = '/', domain, sameSite = 'Lax', secure = true } = options;

  const expiresDate = new Date();
  expiresDate.setTime(expiresDate.getTime() + days * 24 * 60 * 60 * 1000);

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expiresDate.toUTCString()}; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  cookieString += `; SameSite=${sameSite}`;

  if (secure) {
    cookieString += '; Secure';
  }

  document.cookie = cookieString;
}

/**
 * Gets a cookie by name
 */
export function getCookie(name: string): string | null {
  if (typeof window === 'undefined') return null;

  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
}

/**
 * Removes a cookie by name, with optional domain for subdomain cookies
 */
export function removeCookie(name: string, path = '/', domain?: string): void {
  if (typeof window === 'undefined') return;

  let cookieString = `${encodeURIComponent(name)}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax`;
  if (domain) {
    cookieString += `; domain=${domain}`;
  }
  document.cookie = cookieString;
}

/**
 * Sets the JWT token in a cookie (SSO-local, no subdomain sharing)
 */
export function setAuthCookie(token: string): void {
  setCookie('jwt-token', token, {
    days: 7,
    path: '/',
    sameSite: 'Lax',
    secure: window.location.protocol === 'https:',
  });
}

/**
 * Gets the JWT token from cookie
 */
export function getAuthCookie(): string | null {
  return getCookie('jwt-token');
}

/**
 * Removes the JWT token cookie
 */
export function removeAuthCookie(): void {
  removeCookie('jwt-token');
}

/**
 * Sets session_token cookie on .hoomi.social domain so all subdomains can read it.
 * Falls back to no domain (localhost-safe) when not on hoomi.social.
 */
export function setSessionCookie(token: string): void {
  const isHoomiDomain = window.location.hostname.endsWith('.hoomi.social') ||
    window.location.hostname === 'hoomi.social'

  setCookie('session_token', token, {
    days: 7,
    path: '/',
    domain: isHoomiDomain ? '.hoomi.social' : undefined,
    sameSite: 'Lax',
    secure: window.location.protocol === 'https:',
  });

}

/**
 * Gets the session_token cookie (set by SSO, readable across *.hoomi.social)
 */
export function getSessionCookie(): string | null {
  return getCookie('session_token');
}

/**
 * Removes the session_token cookie from .hoomi.social domain
 */
export function removeSessionCookie(): void {
  const isHoomiDomain = window.location.hostname.endsWith('.hoomi.social') ||
    window.location.hostname === 'hoomi.social'

  removeCookie('session_token', '/', isHoomiDomain ? '.hoomi.social' : undefined);
}
